import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { updateNodeData, removeNode, selectNode } from '../slices/flowSlice';
import {
  fetchFacebookPages,
  fetchFacebookForms,
  fetchFormFields,
  subscribeFacebookPage,
  clearForms,
  clearFormFields,
  clearSubscribeSuccess,
} from '../slices/facebookSlice';
import {
  HiOutlineX, HiOutlineTrash, HiOutlineRefresh, HiOutlineCheckCircle,
  HiOutlineExclamationCircle, HiOutlineLightningBolt, HiOutlineChevronDown,
  HiOutlineDuplicate, HiOutlineInformationCircle, HiOutlinePlus,
} from 'react-icons/hi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FB_FIELD_LABELS = {
  FULL_NAME: 'Full Name', EMAIL: 'Email', PHONE_NUMBER: 'Phone',
  CITY: 'City', STATE: 'State', COUNTRY: 'Country',
  COMPANY_NAME: 'Company', JOB_TITLE: 'Job Title',
  WORK_EMAIL: 'Work Email', WORK_PHONE_NUMBER: 'Work Phone',
  ZIP_CODE: 'Zip Code', STREET_ADDRESS: 'Street Address',
  DATE_OF_BIRTH: 'Date of Birth', GENDER: 'Gender',
};

const FB_DEFAULT_KEYS = {
  FULL_NAME: 'name', EMAIL: 'email', PHONE_NUMBER: 'phone',
  CITY: 'city', STATE: 'state', COUNTRY: 'country',
  COMPANY_NAME: 'company', JOB_TITLE: 'jobTitle',
  WORK_EMAIL: 'workEmail', WORK_PHONE_NUMBER: 'workPhone',
  ZIP_CODE: 'zipCode', STREET_ADDRESS: 'address',
  DATE_OF_BIRTH: 'dob', GENDER: 'gender',
};

const genId = () => Math.random().toString(36).slice(2, 9);

/** Convert a plain object to an array of { id, key, value } rows */
const objectToRows = (obj) =>
  Object.entries(obj || {}).map(([k, v]) => ({ id: genId(), key: k, value: String(v) }));

/** Convert rows back to a plain object, skipping blank keys */
const rowsToObject = (rows) => {
  const obj = {};
  (rows || []).forEach(({ key, value }) => { if (key.trim()) obj[key.trim()] = value; });
  return obj;
};

/** Collect available {{lead.X}} entries from the FB trigger node */
function getLeadVarEntries(nodes) {
  const fbNode = nodes.find((n) => (n.data?.type || n.type) === 'facebookTrigger');
  const fieldMapping = fbNode?.data?.config?.fieldMapping;
  if (fieldMapping && Object.keys(fieldMapping).length > 0) {
    return Object.entries(fieldMapping)
      .filter(([, v]) => v)
      .map(([fbKey, outputKey]) => ({ label: FB_FIELD_LABELS[fbKey] || fbKey, key: outputKey }));
  }
  return Object.entries(FB_DEFAULT_KEYS).map(([fbKey, outputKey]) => ({
    label: FB_FIELD_LABELS[fbKey] || fbKey, key: outputKey,
  }));
}

// ─── Variable Picker Button ───────────────────────────────────────────────────
// A small inline button that opens a dropdown of {{lead.X}} chips.
// onInsert(text) is called with the chosen variable string.

function VarPickerButton({ onInsert, nodes }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const entries = getLeadVarEntries(nodes);


  const handleClick = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const close = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setOpen(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={handleClick}
        title="Insert lead variable"
        className="px-1.5 py-1 rounded border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 text-[10px] font-mono text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 transition"
      >
        &#123;x&#125;
      </button>
      {open && (
      <div className="fixed z-[9999] w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1 max-h-52 overflow-y-auto">
          <div className="px-3 py-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-800">
            Insert variable
          </div>
          {entries.map(({ label, key }) => (
            <button
              key={key}
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition"
              onClick={() => { onInsert('{{lead.' + key + '}}'); setOpen(false); }}
            >
              <span className="font-mono text-[10px] text-indigo-600 dark:text-indigo-300">{'{{lead.' + key + '}}'}</span>
              <span className="ml-1.5 text-[10px] text-gray-400">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Key-Value Editor ─────────────────────────────────────────────────────────
// Universal row-based key/value editor used for headers, body fields, query params.

function KeyValueEditor({ rows, onChange, keyPlaceholder = 'Key', valuePlaceholder = 'Value', nodes, showVarPicker = false }) {
  const updateRow = (id, field, val) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const removeRow = (id) => onChange(rows.filter((r) => r.id !== id));
  const addRow = () => onChange([...rows, { id: genId(), key: '', value: '' }]);

const cellClass =
  'px-1.5 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[10px] focus:ring-1 focus:ring-brand-500 outline-none w-full placeholder-gray-400 dark:placeholder-gray-600';
  return (
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-1.5">
          <input
           className={cellClass + ' w-20 flex-shrink-0 font-mono'}
            value={row.key}
            onChange={(e) => updateRow(row.id, 'key', e.target.value)}
            placeholder={keyPlaceholder}
          />
          <div className="flex flex-1 items-center gap-1">
            <input
              className={cellClass + ' flex-1 font-mono w-[10px]'}
              value={row.value}
              onChange={(e) => updateRow(row.id, 'value', e.target.value)}
              placeholder={valuePlaceholder}
            />
            {showVarPicker && (
              <VarPickerButton
                nodes={nodes}
                onInsert={(v) => updateRow(row.id, 'value', row.value + v)}
              />
            )}
          </div>
          <button
            type="button"
            onClick={() => removeRow(row.id)}
            className="p-1 rounded text-gray-300 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition flex-shrink-0"
          >
            <HiOutlineX size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-medium mt-1 transition"
      >
        <HiOutlinePlus size={12} /> Add row
      </button>
    </div>
  );
}

// ─── Sheet Columns Editor ─────────────────────────────────────────────────────
// Like KeyValueEditor but for Google Sheets: label (column name hint) + value.

function SheetColumnsEditor({ rows, onChange, nodes }) {
  const updateRow = (id, field, val) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const removeRow = (id) => onChange(rows.filter((r) => r.id !== id));
  const addRow = () => onChange([...rows, { id: genId(), header: '', value: '' }]);

  const cellClass =
    'px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-1 focus:ring-brand-500 outline-none w-full placeholder-gray-400 dark:placeholder-gray-600';

  return (
    <div className="space-y-1.5">
      {rows.length === 0 ? null : (
        <div className="grid text-[9px] font-semibold uppercase tracking-wide text-gray-400 mb-1" style={{gridTemplateColumns:'1fr 1fr 20px'}}>
          <span>Column (label)</span><span>Value</span><span/>
        </div>
      )}
      {rows.map((row) => (
        <div key={row.id} className="grid gap-1.5 items-center" style={{gridTemplateColumns:'1fr 1fr 20px'}}>
          <input
            className={cellClass}
            value={row.header}
            onChange={(e) => updateRow(row.id, 'header', e.target.value)}
            placeholder="e.g. Name"
          />
          <div className="flex items-center gap-1">
            <input
              className={cellClass + ' font-mono flex-1'}
              value={row.value}
              onChange={(e) => updateRow(row.id, 'value', e.target.value)}
              placeholder="Value or {{lead.x}}"
            />
            <VarPickerButton
              nodes={nodes}
              onInsert={(v) => updateRow(row.id, 'value', row.value + v)}
            />
          </div>
          <button
            type="button"
            onClick={() => removeRow(row.id)}
            className="p-1 rounded text-gray-300 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
          >
            <HiOutlineX size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-medium mt-1 transition"
      >
        <HiOutlinePlus size={12} /> Add column
      </button>
    </div>
  );
}

// ─── Available Variables Panel ────────────────────────────────────────────────

function AvailableVarsPanel({ nodes }) {
  const [copied, setCopied] = useState(null);
  const entries = getLeadVarEntries(nodes);

  const copy = (key) => {
    navigator.clipboard?.writeText('{{lead.' + key + '}}');
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <HiOutlineInformationCircle size={13} className="text-indigo-500" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
          Available lead variables
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(({ label, key }) => (
          <button
            key={key}
            onClick={() => copy(key)}
            title={'Click to copy — ' + label}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white dark:bg-gray-900 border border-indigo-200 dark:border-indigo-700 hover:border-indigo-400 transition group"
          >
            <code className="font-mono text-[10px] text-indigo-700 dark:text-indigo-300">
              {copied === key ? 'Copied!' : '{{lead.' + key + '}}'}
            </code>
            <HiOutlineDuplicate size={9} className="text-indigo-400 opacity-0 group-hover:opacity-100 transition" />
          </button>
        ))}
      </div>
      <p className="text-[10px] text-indigo-400 mt-1.5">Click to copy. Use the &#123;x&#125; button on any value field to insert directly.</p>
    </div>
  );
}

// ─── Field mapper (FB → output key) ──────────────────────────────────────────

function FieldMapper({ fields, mapping, onMappingChange }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400">
        Field Mapping
        <span className="ml-1.5 text-gray-400 font-normal normal-case">(FB field → output key)</span>
      </label>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="grid grid-cols-2 gap-px bg-gray-100 dark:bg-gray-800 text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-3 py-1.5">
          <span>Facebook Field</span><span>Output Key</span>
        </div>
        {fields.map((field) => {
          const key = field.key || field.type;
          const label = FB_FIELD_LABELS[key] || field.label || key;
          const defaultKey = FB_DEFAULT_KEYS[key] || key.toLowerCase().replace(/_/g, '');
          return (
            <div key={key} className="grid grid-cols-2 gap-2 items-center px-3 py-1.5 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs text-gray-700 dark:text-gray-300 truncate" title={label}>{label}</div>
              <input
                type="text"
                className="w-full px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs font-mono focus:ring-1 focus:ring-brand-500 outline-none"
                value={mapping?.[key] ?? defaultKey}
                onChange={(e) => onMappingChange(key, e.target.value)}
                placeholder={defaultKey}
              />
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5">
        Reference with <code className="text-brand-500 font-mono">{'{{lead.KEY}}'}</code> in downstream nodes.
      </p>
    </div>
  );
}

// ─── Facebook Trigger Config ──────────────────────────────────────────────────

function FacebookTriggerConfig({ config, update, scenarioId, facebookConnected }) {
  const dispatch = useDispatch();
  const {
    pages, forms, formFields,
    pagesLoading, formsLoading, fieldsLoading,
    subscribing, subscribeSuccess, error: fbError,
  } = useSelector((s) => s.facebook);

  const selectedPageId = config.pageId || '';
  const selectedFormId = config.formId || '';
  const fieldMapping = config.fieldMapping || {};

  useEffect(() => {
    if (facebookConnected && pages.length === 0 && !pagesLoading) {
      dispatch(fetchFacebookPages());
    }
  }, [facebookConnected, pages.length, pagesLoading, dispatch]);

  useEffect(() => {
    if (facebookConnected && selectedPageId && forms.length === 0 && !formsLoading) {
      dispatch(fetchFacebookForms(selectedPageId));
    }
  }, [facebookConnected, selectedPageId]);

  useEffect(() => {
    if (facebookConnected && selectedFormId && selectedPageId && formFields.length === 0 && !fieldsLoading) {
      dispatch(fetchFormFields({ formId: selectedFormId, pageId: selectedPageId }));
    }
  }, [facebookConnected, selectedFormId, selectedPageId]);

  useEffect(() => {
    if (subscribeSuccess) {
      const t = setTimeout(() => dispatch(clearSubscribeSuccess()), 3000);
      return () => clearTimeout(t);
    }
  }, [subscribeSuccess, dispatch]);

  const handlePageChange = useCallback((pageId) => {
    const page = pages.find((p) => p.id === pageId);
    update({ pageId, pageName: page?.name || '', formId: '', formName: '', fieldMapping: {} });
    dispatch(clearForms());
    if (pageId) dispatch(fetchFacebookForms(pageId));
  }, [pages, update, dispatch]);

  const handleFormChange = useCallback((formId) => {
    const form = forms.find((f) => f.id === formId);
    update({ formId, formName: form?.name || '', fieldMapping: {} });
    dispatch(clearFormFields());
    if (formId && selectedPageId) dispatch(fetchFormFields({ formId, pageId: selectedPageId }));
  }, [forms, selectedPageId, update, dispatch]);

  const handleMappingChange = useCallback((fbKey, outputKey) => {
    update({ fieldMapping: { ...fieldMapping, [fbKey]: outputKey } });
  }, [fieldMapping, update]);

  if (!facebookConnected) {
    return (
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-2">
        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 text-xs font-medium">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 shrink-0">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Facebook not connected
        </div>
        <p className="text-xs text-blue-600 dark:text-blue-400">Connect your Facebook account in Settings to select pages and lead forms.</p>
        <Link to="/settings" className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300 underline underline-offset-2">Go to Settings →</Link>
      </div>
    );
  }

  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
  const webhookUrl = apiBase + '/webhook/facebook/' + (scenarioId || '{scenarioId}');

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Facebook Page</span>
          <button onClick={() => dispatch(fetchFacebookPages())} disabled={pagesLoading} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition disabled:opacity-40" title="Refresh">
            <HiOutlineRefresh size={12} className={pagesLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="relative">
          <select className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-brand-500 outline-none appearance-none disabled:opacity-50" value={selectedPageId} onChange={(e) => handlePageChange(e.target.value)} disabled={pagesLoading}>
            <option value="">{pagesLoading ? 'Loading pages…' : pages.length === 0 ? 'No pages found' : 'Select a page'}</option>
            {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <HiOutlineChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
        </div>
      </div>

      {selectedPageId && (
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Lead Form</label>
          <div className="relative">
            <select className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-brand-500 outline-none appearance-none disabled:opacity-50" value={selectedFormId} onChange={(e) => handleFormChange(e.target.value)} disabled={formsLoading}>
              <option value="">{formsLoading ? 'Loading forms…' : forms.length === 0 ? 'No lead forms found' : 'Select a form'}</option>
              {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <HiOutlineChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
          </div>
        </div>
      )}

      {fieldsLoading && <div className="flex items-center gap-1.5 text-xs text-gray-400"><HiOutlineRefresh size={12} className="animate-spin" /> Loading form fields…</div>}
      {!fieldsLoading && formFields.length > 0 && <FieldMapper fields={formFields} mapping={fieldMapping} onMappingChange={handleMappingChange} />}
      {!fieldsLoading && selectedFormId && formFields.length === 0 && <p className="text-[10px] text-gray-400">Select a form to load its fields and configure mapping.</p>}

      {selectedPageId && (
        <div className="space-y-1">
          <button onClick={() => dispatch(subscribeFacebookPage(selectedPageId))} disabled={subscribing || subscribeSuccess} className={'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition ' + (subscribeSuccess ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700' : 'bg-[#1877F2] hover:bg-[#1664d8] text-white disabled:opacity-50')}>
            {subscribeSuccess ? <><HiOutlineCheckCircle size={14} /> Page subscribed</> : subscribing ? <><HiOutlineRefresh size={14} className="animate-spin" /> Subscribing…</> : <><HiOutlineLightningBolt size={14} /> Subscribe Page to Webhook</>}
          </button>
          {fbError && <p className="text-[10px] text-red-500 flex items-center gap-1"><HiOutlineExclamationCircle size={12} /> {fbError}</p>}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Webhook URL</div>
        <code className="block text-[10px] font-mono break-all text-gray-700 dark:text-gray-300 leading-relaxed">{webhookUrl}</code>
      </div>
    </div>
  );
}

// ─── HTTP Request Config ──────────────────────────────────────────────────────

function HttpRequestConfig({ config, update, nodes }) {
  const method = config.method || 'POST';
  const bodyType = config.bodyType || (method === 'GET' ? 'none' : 'fieldmap');
  const headerRows = config.headerRows || (
    method !== 'GET' ? [{ id: genId(), key: 'Content-Type', value: 'application/json' }] : []
  );
  const bodyFields = config.bodyFields || [];
  const bodyRaw = config.bodyRaw || '';

  // Sections collapse state
  const [headersOpen, setHeadersOpen] = useState(true);
  const [bodyOpen, setBodyOpen] = useState(true);

  const BODY_TYPES = ['fieldmap', 'raw', 'none'];
  const BODY_LABELS = { fieldmap: 'Field Map', raw: 'Raw JSON', none: 'None' };

  const onHeadersChange = (rows) => {
    update({ headerRows: rows, headers: rowsToObject(rows) });
  };

  const onBodyFieldsChange = (rows) => {
    const body = rowsToObject(rows);
    update({ bodyFields: rows, body });
  };

  const onBodyRawChange = (raw) => {
    let body = {};
    try { body = JSON.parse(raw); } catch (_) {}
    update({ bodyRaw: raw, body });
  };

  const onMethodChange = (m) => {
    const newBodyType = m === 'GET' ? 'none' : (config.bodyType || 'fieldmap');
    update({ method: m, bodyType: newBodyType });
  };

  const onBodyTypeChange = (t) => update({ bodyType: t });

  const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none text-sm';

  const sectionHeaderClass = 'flex items-center justify-between w-full text-xs font-semibold text-gray-600 dark:text-gray-400 py-1 cursor-pointer select-none';

  return (
    <div className="space-y-4">
      <AvailableVarsPanel nodes={nodes} />

      {/* Method + URL */}
      <div className="flex gap-2">
        <div className="w-24 flex-shrink-0">
          <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Method</label>
          <div className="relative">
            <select className={inputClass + ' appearance-none pr-6 font-mono font-bold text-xs'} value={method} onChange={(e) => onMethodChange(e.target.value)}>
              {['GET','POST','PUT','PATCH','DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <HiOutlineChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">URL</label>
          <input className={inputClass} value={config.url || ''} onChange={(e) => update('url', e.target.value)} placeholder="https://api.example.com/endpoint" />
        </div>
      </div>

      {/* Headers section */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button type="button" className="flex items-center justify-between w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer" onClick={() => setHeadersOpen((o) => !o)}>
          <span>Headers <span className="font-normal text-gray-400">({headerRows.length})</span></span>
          <HiOutlineChevronDown size={12} className={'transition-transform ' + (headersOpen ? '' : '-rotate-90')} />
        </button>
        {headersOpen && (
          <div className="p-3">
            <KeyValueEditor rows={headerRows} onChange={onHeadersChange} keyPlaceholder="Header name" valuePlaceholder="Header value" nodes={nodes} showVarPicker={false} />
          </div>
        )}
      </div>

      {/* Body section (hidden for GET) */}
      {method !== 'GET' && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button type="button" className="flex items-center justify-between w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer" onClick={() => setBodyOpen((o) => !o)}>
            <span>Body</span>
            <HiOutlineChevronDown size={12} className={'transition-transform ' + (bodyOpen ? '' : '-rotate-90')} />
          </button>
          {bodyOpen && (
            <div className="p-3 space-y-3">
              {/* Body type tabs */}
              <div className="flex gap-0 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-fit">
                {BODY_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => onBodyTypeChange(t)}
                    className={'px-3 py-1 text-xs font-medium transition border-r border-gray-200 dark:border-gray-700 last:border-r-0 ' + (bodyType === t ? 'bg-brand-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700')}
                  >
                    {BODY_LABELS[t]}
                  </button>
                ))}
              </div>

              {bodyType === 'fieldmap' && (
                <div>
                  {bodyFields.length === 0 && (
                    <p className="text-[10px] text-gray-400 mb-2">Add key-value pairs. Use the &#123;x&#125; button to insert lead variables.</p>
                  )}
                  <KeyValueEditor rows={bodyFields} onChange={onBodyFieldsChange} keyPlaceholder="Field name" valuePlaceholder="Value or {{lead.x}}" nodes={nodes} showVarPicker={true} />
                </div>
              )}

              {bodyType === 'raw' && (
                <div>
                  <textarea
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-brand-500 outline-none text-xs font-mono h-40"
                    value={bodyRaw}
                    onChange={(e) => onBodyRawChange(e.target.value)}
                    placeholder={'{\n  "name": "{{lead.name}}",\n  "email": "{{lead.email}}"\n}'}
                    spellCheck={false}
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Must be valid JSON. Use the reference panel above for variable names.</p>
                </div>
              )}

              {bodyType === 'none' && (
                <p className="text-[10px] text-gray-400">No body will be sent with this request.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Google Sheets Config ─────────────────────────────────────────────────────

function GoogleSheetsConfig({ config, update, nodes }) {
  const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none text-sm';

  const sheetColumns = config.sheetColumns || [];

  const onColumnsChange = (rows) => {
    const values = rows.map((r) => r.value);
    update({ sheetColumns: rows, values });
  };

  return (
    <div className="space-y-4">
      <AvailableVarsPanel nodes={nodes} />

      <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 text-xs text-green-700 dark:text-green-300">
        Connect your Google account in <strong>Settings</strong> before using this node.
      </div>

      <div>
        <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Spreadsheet ID</label>
        <input className={inputClass} value={config.spreadsheetId || ''} onChange={(e) => update('spreadsheetId', e.target.value)} placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
        <p className="text-[10px] text-gray-400 mt-1">From the spreadsheet URL: …/spreadsheets/d/<strong>ID</strong>/edit</p>
      </div>

      <div>
        <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Sheet Tab Name</label>
        <input className={inputClass} value={config.sheetName || ''} onChange={(e) => update('sheetName', e.target.value)} placeholder="Sheet1" />
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
            Row Columns
            <span className="ml-1.5 font-normal text-gray-400">(one column per row appended)</span>
          </span>
        </div>
        <div className="p-3">
          {sheetColumns.length === 0 && (
            <p className="text-[10px] text-gray-400 mb-2">Add each column value. Use the &#123;x&#125; button to insert lead variables.</p>
          )}
          <SheetColumnsEditor rows={sheetColumns} onChange={onColumnsChange} nodes={nodes} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

/** Normalize a loaded config so UI row-array fields are always populated. */
function normalizeConfig(raw) {
  const cfg = { ...raw };
  if (!cfg.headerRows && cfg.headers && typeof cfg.headers === 'object') {
    cfg.headerRows = objectToRows(cfg.headers);
  }
  if (!cfg.bodyFields && cfg.body && typeof cfg.body === 'object') {
    cfg.bodyFields = objectToRows(cfg.body);
    if (!cfg.bodyType) cfg.bodyType = 'fieldmap';
  }
  if (!cfg.sheetColumns && Array.isArray(cfg.values) && cfg.values.length > 0) {
    cfg.sheetColumns = cfg.values.map((v, i) => ({ id: genId(), header: 'Column ' + (i + 1), value: String(v) }));
  }
  return cfg;
}

export default function NodeConfigPanel() {
  const dispatch = useDispatch();
  const { id: scenarioId } = useParams();
  const { selectedNode, nodes } = useSelector((s) => s.flow);
  const user = useSelector((s) => s.auth.user);
  const [config, setConfig] = useState({});

  useEffect(() => {
    if (selectedNode) setConfig(normalizeConfig(selectedNode.data?.config || {}));
  }, [selectedNode?.id]);


  const nodeType = selectedNode.data?.type || selectedNode.type;

  const update = (keyOrChanges, value) => {
    setConfig((prev) => {
      const newConfig =
        typeof keyOrChanges === 'object'
          ? { ...prev, ...keyOrChanges }
          : { ...prev, [keyOrChanges]: value };
      dispatch(updateNodeData({ id: selectedNode.id, data: { config: newConfig } }));
      return newConfig;
    });
  };

  const NODE_META = {
    facebookTrigger: { label: 'Facebook Lead Trigger', color: 'text-[#1877F2] bg-blue-50 dark:bg-blue-900/20' },
    httpRequest:     { label: 'HTTP Request',           color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
    googleSheets:    { label: 'Google Sheets',          color: 'text-green-600 bg-green-50 dark:bg-green-900/20' },
  };
  const meta = NODE_META[nodeType] || { label: nodeType, color: 'text-gray-500 bg-gray-100' };

  return (
    <div className="w-80 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 flex flex-col animate-fade-in text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
        <span className={'px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ' + meta.color}>{nodeType}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => dispatch(removeNode(selectedNode.id))} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition" title="Delete node"><HiOutlineTrash size={15} /></button>
          <button onClick={() => dispatch(selectNode(null))} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition"><HiOutlineX size={15} /></button>
        </div>
      </div>
      <div className="px-4 pt-3 pb-1 shrink-0">
        <h3 className="font-semibold text-sm">{meta.label}</h3>
      </div>

      <div className="p-4 pt-2 flex-1 overflow-y-auto">
        {nodeType === 'facebookTrigger' && (
          <FacebookTriggerConfig config={config} update={update} scenarioId={scenarioId} facebookConnected={user?.facebookConnected} />
        )}
        {nodeType === 'httpRequest' && (
          <HttpRequestConfig config={config} update={update} nodes={nodes} />
        )}
        {nodeType === 'googleSheets' && (
          <GoogleSheetsConfig config={config} update={update} nodes={nodes} />
        )}
      </div>
    </div>
  );
}
